<template>
	<div>
		<div class="text-right md:flex md:justify-between md:align-items-center w75">
			<EbHeading v-if="!isMobile && !isSmallScreen" level="h1" class="eb-page-heading">Mon compte</EbHeading>
			<a :href="publicProfileUrl" class="eb-button secondary small icon-after" target="_blank" rel="noreferrer noopener" title="Voir ma fiche en ligne - Nouvelle fenêtre">Voir ma fiche en ligne <font-awesome-icon :icon="['fas', 'external-link-alt']" /></a> 
		</div>
		<div class="eb-content-default w75">
			<EbTabs ariaLabel="Sommaire des onglets pour mon compte" :tabs="[{name: 'Profil'}, {name: 'Paramètres'}, {name: 'Contacts'}]" @changeTab="changeStep"></EbTabs>
			<section v-if="page === 0 && agrement" id="panel-0" role="tabpanel" tabindex="0" aria-labelledby="tab-0">
				<EbHeading v-if="isMobile || isSmallScreen" level="h2" class="eb-box-heading">Profil</EbHeading>
				<p class="text-smaller">Ces informations sont transmises par le Département de Maine-et-Loire et mises à jour toutes les nuits :</p>
				<div class="eb-exergue flex gap-5">
						<font-awesome-icon :icon="['fas', 'map-marker-alt']" />
						{{ adresse.numero }}
						{{ adresse.libelleAdresse }}</br>
						{{ adresse.communeDeleguee }}</br>
						{{ adresse.codePostal }}
						{{ adresse.commune }}
				</div>
				
				<div class="eb-exergue eb-lighter-blue-bgcolor">
					<EbHeading level="h2" class="eb-box-heading">Mon agrément</EbHeading>
					<ul v-if="agrement.types">
						<li v-for="t in agrement.types">
							{{ t.nombreEnfants }}
							<span v-if="t.nombreEnfants > 1">enfants agés de</span>
							<span v-else>enfant agé de</span>
							{{ t.trancheAge }}
						</li>
					</ul>
					
					<dl>
						<dt>1er agrément :</dt>
						<dd>
							<font-awesome-icon :icon="['far', 'calendar-alt']" /> <strong><time :datetime="agrement.datePremierAgrement">{{ getLongDate(agrement.datePremierAgrement) }}</time></strong>
						</dd>
						<dt>Fin de l’agrément :</dt>
						<dd>
							<font-awesome-icon :icon="['far', 'calendar-alt']" /> <strong><time :datetime="agrement.dateFinAgrement">{{ getLongDate(agrement.dateFinAgrement) }}</time></strong>
						</dd>
						<dt v-if="agrement.dateDernierRenouvellement">Dernier renouvellement :</dt>
						<dd v-if="agrement.dateDernierRenouvellement">
							<font-awesome-icon :icon="['far', 'calendar-alt']" /> <strong><time :datetime="agrement.dateDernierRenouvellement">{{ getLongDate(agrement.dateDernierRenouvellement) }}</time></strong>
						</dd>
						<br>
						<dt>Type d’exercice :</dt>
						<dd v-if="agrement.exerceMam"><strong>en Maison d'Assistants Maternels</strong></dd>
						<dd v-else><strong>à domicile</strong></dd>
						
						<dt v-if="agrement.exerceMam">Nom de la Maison d’Assistants Maternels :</dt>
						<dd v-if="agrement.exerceMam"><strong>{{ adresse.complementAdresse }}</strong></dd>
					</dl>
				</div>
				
				<form action="#" @submit.prevent="setProfil">
					<EbBanner :isActive="isBannerActive" :type="bannerType" :timestamp="bannerTimestamp">{{ bannerContent }}</EbBanner>
					
					<EbCheckbox label='J‘accepte que mes données personnelles apparaissent sur "assmat.maine-et-loire.fr" : elles seront accessibles à toute personne le consultant.' v-model="accepteApparaitreAssmat49"></EbCheckbox>
					<p>Vous pouvez modifier ou retirer les informations vous concernant à partir de votre compte personnel. Pour en savoir plus sur gestion de vos données, vous pouvez <a href="https://assmat.maine-et-loire.fr/donnees-personnelles" target="_blank" rel="noreferrer noopener" title="Consulter notre politique de protection des données - Nouvelle fenêtre">consulter notre politique de protection des données</a>. Pour exercer vos droits ou pour toute question, <a href="mailto:dpd@maine-et-loire.fr" target="_blank" title="Contacter notre déléguée à la protection des données - Fenêtre de messagerie">contactez notre déléguée à la protection des données</a> : dpd@maine-et-loire.fr</p>
					<EbButton type="submit">Enregistrer</EbButton>
				</form>
			</section>
			
			<section v-if="page == 2" id="panel-2" role="tabpanel" tabindex="0" aria-labelledby="tab-2">
				<EbHeading v-if="isMobile || isSmallScreen" level="h2" class="eb-box-heading">Contacts</EbHeading>
				
				<p class="text-smaller">Vos interlocuteurs à privilégier sur votre territoire.</p>
				
				<EbHeading level="h2" class="eb-box-heading">Maison départementale des solidarités</EbHeading>
				<EbLoader v-if="isLoading"></EbLoader>
				<address v-if="!isLoading && contactMDS">
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'map-marker-alt']" />
						<abbr title="Maison départementale des solidarités">MDS</abbr> {{ contactMDS.libelle }}<br />
						{{ contactMDS.libelleAdresse }}<br />
						{{ contactMDS.codePostal }} {{ contactMDS.commune }}
					</div>
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'phone']" />
						<a :title="getPhoneTitle('la MDS', contactMDS.numeroTelephone1)" :href="getPhoneLink(contactMDS.numeroTelephone1)">{{ contactMDS.numeroTelephone1 }}</a>
					</div>
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'phone']" />
						<a v-if="contactMDS.numeroTelephone2" :title="getPhoneTitle('la MDS', contactMDS.numeroTelephone2)" :href="getPhoneLink(contactMDS.numeroTelephone2)">
							{{ contactMDS.numeroTelephone2 }}
						</a>
					</div>
					<a :href="getMailLink(contactMDS.courrielPmi)" :title="getMailTitle(contactMDS.courrielPmi)" target="_blank" rel="noreferrer noopener" class="flex gap-5 align-items">
						<font-awesome-icon :icon="['fas', 'envelope']" />{{ contactMDS.courrielPmi }}
					</a>
				</address>
				<p v-if="!isLoading && !contactMDS">Aucune information de rattachement à une MDS ne correspond à votre profil.</p>
				
				<EbHeading level="h2" class="eb-box-heading">Relais petite enfance</EbHeading>
				<EbLoader v-if="isLoading"></EbLoader>
				<address v-if="!isLoading && contactRAM">
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'map-marker-alt']" />
						{{ contactRAM.libelle }}<br />
						{{ contactRAM.adresse }}<br />
						{{ contactRAM.codePostal }} {{ contactRAM.commune }}
					</div>
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'phone']" />
						<a :title="getPhoneTitle('le RPE', contactRAM.telephone)" :href="getPhoneLink(contactRAM.telephone)">{{ contactRAM.telephone }}</a>
					</div>
					<div class="flex gap-5">
						<font-awesome-icon :icon="['fas', 'envelope']" />
						<a :href="getMailLink(contactRAM.courriel)" :title="getMailTitle(contactRAM.courriel)" target="_blank" rel="noreferrer noopener">{{ contactRAM.courriel }}</a>
					</div>
				</address>
				<p v-if="!isLoading && !contactRAM">Aucune information de rattachement à un RAM ne correspond à votre profil.</p>
				
				
				<EbHeading level="h2" class="eb-box-heading">Contacter le Département</EbHeading>
				<EbLoader v-if="isLoading"></EbLoader>
				
				<EbBanner :isActive="isBannerActive" :type="bannerType" :timestamp="bannerTimestamp">{{ bannerContent }}</EbBanner>
				
				<form action="#" @submit.prevent="sendMessage">
					<div class="eb-select">
						<EbLabel forId="thematique" text="Ma demande concerne :" required>
							<select id="thematique" class="eb-select-solid" required v-model="selectedThematiques">
								<option disabled selected value="">Sélectionner une thématique</option>
								<option v-for="t in thematiques" :value="t.id">
									{{ t.libelle }}
								</option>
							</select>
						</EbLabel>
					</div>
					<EbTextarea label="Mon message :" :cols="40" :rows="10" v-model="message" required></EbTextarea>
					<EbButton type="submit">Envoyer mon message</EbButton>
				</form>
			</section>
			
			<section v-if="page == 1" id="panel-1" role="tabpanel" tabindex="0" aria-labelledby="tab-1">
				<EbHeading level="h2" class="eb-box-heading">Modifier mon courriel</EbHeading>
				
				<EbBanner :isActive="isBannerActive" :type="bannerType" :timestamp="bannerTimestamp">{{ bannerContent }}</EbBanner>
				
				<p>Votre courriel utilisé actuellement pour vous connecter est : <strong>{{courriel}}</strong>.</p>
				
				<form action="#" @submit.prevent="setNewEmail()">
					<EbInput type="email" v-model="newEmail" required label="Nouveau courriel" width="expand"></EbInput>
					
					<div class="eb-button-group"><EbButton type="submit">Enregistrer mon nouveau courriel</EbButton></div>
				</form>
				
				<EbHeading level="h2" class="eb-box-heading">Modifier mon mot de passe</EbHeading>
				
				<EbBanner :isActive="isBannerPasswordActive" :type="bannerPasswordType" :timestamp="bannerPasswordTimestamp">{{ bannerPasswordContent }}</EbBanner>
				
				<form action="#" @submit.prevent="compareChangingPasswords() && setNewPassword()">
					<EbPassword type="password" v-model="actualPassword" required label="Mot de passe actuel" width="expand"></EbPassword>
					
					<EbPassword type="password" v-model="newPassword" required label="Nouveau mot de passe" needCondition width="expand"></EbPassword>
					<div class="eb-button-group"><EbButton type="submit">Enregistrer mon nouveau mot de passe</EbButton></div>
				</form>
			</section>
		</div>
	</div>
</template>

<script>
export default {
	name: "monCompte",
	data() {
		return {
			page: 0,
			contactMDS: null,
			contactRAM: null,
			contactsError: false,
			agrement: null,
			adresse: null,
			courriel: null,
			newEmail: null,
			emailSuccess: false,
			setNewEmailError: false,
			emailExists: false,
			accepteApparaitreMonEnfantFr: null,
			accepteApparaitreAssmat49: null,
			actualPassword: "",
			newPassword: "",
			isLoading: false,
			thematiques: null,
			selectedThematiques: '',
			message: '',
			isMessageSuccess: false,
			isMessageError: false,
			publicProfileUrl: '',
			
			isBannerActive: false,
			bannerTimestamp: '',
			bannerType: null,
			bannerContent: '',
			
			isBannerPasswordActive: false,
			bannerPasswordTimestamp: '',
			bannerPasswordType: null,
			bannerPasswordContent: ''      
		}
	},
	mounted() {
		this.getProfil()
		this.publicProfileUrl = this.getPublicProfileLink()
	},
	watch: {
		page: function() {
			this.isBannerActive = false
			
			if (this.page == 2) {
				this.getContacts()
				this.getThematiques()
			}
			else if(this.page == 0) {
				this.getProfil()
			}
		}
	},
	methods: {
		compareChangingPasswords: function() {
			this.isBannerActive = false
			
			if (actualPassword !== newPassword) {
				return true
			}
			
			this.isBannerActive = true
			this.bannerTimestamp = this.getTime()
			this.bannerType = 'error'
			this.bannerContent = 'Les champs "Code d’activation" et "Nouveau mot de passe" doivent être différents, veuillez vérifier votre saisie.'
			return false
		},
		getContacts: async function() {
			this.isLoading = true
			this.isBannerActive = false
			this.bannerTimestamp = this.getTime()
			
			await this.$http
			.get("assmat/contacts")
			.then(resp => {
				this.contactMDS = resp.data.mds
				this.contactRAM = resp.data.ram
				this.isLoading = false
			})
			.catch(error => {
				this.isLoading = false
				this.isBannerActive = true
			})
		},
		getProfil: async function() {
			this.isBannerActive = false
			await this.$http.get("assmat/profil").then(resp => {
				this.adresse = resp.data.adresse
				this.courriel = resp.data.email
				this.agrement = resp.data.agrement
				this.accepteApparaitreMonEnfantFr = resp.data.accepteApparaitreMonEnfantFr
				this.accepteApparaitreAssmat49 = resp.data.accepteApparaitreAssmat49
			})
		},
		setProfil: async function() {
			this.isBannerActive = false
			this.bannerTimestamp = this.getTime()
			
			await this.$http
			.put("assmat/profil", {
				adresse: this.adresse,
				agrement: this.agrement,
				accepteApparaitreMonEnfantFr: this.accepteApparaitreMonEnfantFr,
				accepteApparaitreAssmat49: this.accepteApparaitreAssmat49,
			})
			.then(resp => {
				this.isBannerActive = true
				this.bannerType = 'success'
				this.bannerContent = 'Vos préférences ont bien été prises en compte.'
			})
			.catch(error => {
				this.isBannerActive = true
				this.bannerType = 'error'
				this.bannerContent = 'Une erreur est survenue lors de l’enregistrement de vos préférences, veuillez rééssayer ultérieurement.'
			})
		},
		setNewPassword: async function() {
			this.isBannerActive = false
			
			await this.$http
			.put("authentification/changeMotDePasse", {
				newPassword: this.newPassword,
				password: this.actualPassword,
			})
			.then(resp => {
				this.isBannerPasswordActive = true
				this.bannerPasswordTimestamp = this.getTime()
				this.bannerPasswordType = 'success'
				this.bannerPasswordContent = 'Votre changement de mot de passe a bien été pris en compte.'
			})
			.catch(error => {
				if (error.response.status === 400) {
					this.isBannerPasswordActive = true
					this.bannerPasswordTimestamp = this.getTime()
					this.bannerPasswordType = 'error'
					this.bannerPasswordContent = 'Votre mot de passe actuel n’est pas le bon, veuillez réessayer.'
				}
			})
		},
		setNewEmail: async function() {
			this.isBannerActive = false
			
			await this.$http
			.put("authentification/changeCourriel", {
				courriel: this.newEmail
			})
			.then(resp => {
				this.courriel = this.newEmail
				this.newEmail = ''
				
				this.isBannerActive = true
				this.bannerTimestamp = this.getTime()
				this.bannerType = 'success'
				this.bannerContent = 'Votre changement de courriel a bien été pris en compte.'
			})
			.catch(error => {
				if (error.response.status === 400) {
					this.isBannerActive = true
					this.bannerTimestamp = this.getTime()
					this.bannerType = 'error'
					this.bannerContent = 'Une erreur est survenue, veuillez réessayer ultérieurement.'
				}
				else if (error.response.status === 409) {
					this.isBannerActive = true
					this.bannerTimestamp = this.getTime()
					this.bannerType = 'error'
					this.bannerContent = 'Le courriel renseigné existe déjà pour un compte, veuillez en choisir un autre.'
				}
			})
		},
		getThematiques: async function() {
			this.isLoading = true
			await this.$http.get('thematiques')
			.then(resp => {
				this.thematiques = resp.data
			})
			.catch(error => {
				this.contactsError = true
			})
		},
		changeThematiques: function(val) {
			this.selectedThematiques = val
		},
		sendMessage: async function() {
			this.isBannerActive = true
			this.bannerTimestamp = this.getTime()
			
			await this.$http
			.post("assmat/contacts", {
				idThematique: this.selectedThematiques,
				message: this.message
			})
			.then(resp => {
				this.isBannerActive = true
				this.bannerType = 'success'
				this.bannerContent = 'Votre message a été envoyé avec succès.'
        
        this.selectedThematiques = ''
        this.message = null
			})
			.catch(error => {
				this.isBannerActive = true
				this.bannerType = 'error'
				this.bannerContent = 'Une erreur est survenue lors de l’envoi de votre message, veuillez réessayer ultérieurement.'
			})
		}
	},
}
</script>
